/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { connect } from 'react-redux';
import * as _ from 'lodash';

import { FormattedMessage } from 'react-intl';
import { MailToLink, ExternalLink } from '../../../components/TextLink';
import Spacer from '../../../components/Spacer';

import ExistingDataConnection from './ExistingDataConnection';
import NoExistingDataConnection from './NoExistingDataConnection';
import ProfileService from '../../../../apiServices/ProfileService';
import ContentLoader from '../../../components/ContentLoader';
import { GrayCard, CardSection, EmptyCard } from '../../../components/card/Card';
import IconWarning from '../../../components/icons/IconWarning';
import { actions as integrationsActions } from '../../../redux/ducks/integrations';
import LegalDocumentLinks from '../../../components/LegalDocumentLinks';
import ContentWrapper from '../../../components/ContentWrapper';

const SUPPORTED_LANGUAGES = ['en'];

interface DataConnectionAllowedState {
  hasIntegration: boolean;
  isLoaded: boolean;
  config: {};
  hasError: boolean;
  isMedfusionLoaded: boolean;
}
interface DataConnectionAllowedProps {
  preferredLanguage: string;
  isMainProfile: boolean;
  profileId: number;
  profileName: string;
  requestIntegrations(profileId): Promise<any>;
}

function getMillSecUntilExpire(accessToken) {
  if (!accessToken) { return 100; }

  const mainPart = accessToken.split('.')[1];
  const expTime = JSON.parse(window.atob(mainPart)).exp;
  const msUntilExp = (expTime * 1000) - Date.now();

  return msUntilExp <= 0 ? 100 : msUntilExp;
}

class DataConnectionAllowed extends React.Component<DataConnectionAllowedProps, DataConnectionAllowedState> {

  refreshToken: any;

  state: DataConnectionAllowedState = {
    isLoaded: false,
    hasIntegration: false,
    config: null,
    hasError: false,
    isMedfusionLoaded: false,
  };

  getConfig = () => {
    ProfileService.getMedfusionConfig(this.props.profileId)
      .then(config => {
        this.setState({
          config,
          isLoaded: true,
          hasIntegration: true,
          hasError: false,
        }, this.checkToken);
      })
      .catch(err => {
        this.setState({ isLoaded: true, hasError: true });
      });
  }

  checkToken = () => {
    const accessToken = _.get(this, 'state.config.accessToken' , '');
    const msUntilExp = getMillSecUntilExpire(accessToken);

    if (this.refreshToken) {
      window.clearInterval(this.refreshToken);
      this.refreshToken = null;
    }

    if (msUntilExp > 10000) {
      this.refreshToken = setTimeout(this.checkToken, msUntilExp);
      return;
    }

    this.setState({
      isLoaded: false,
    }, this.getConfig);
  }

  componentWillUnmount() {
    if (this.refreshToken) {
      window.clearInterval(this.refreshToken);
      this.refreshToken = null;
    }

    window.removeEventListener('resume', this.checkToken);
  }

  componentWillMount() {
    if (IS_ANDROID_BUILD) {
      document.addEventListener('resume', this.checkToken, false);
    }

    this.props.requestIntegrations(this.props.profileId)
      .then(res => {
        const hasIntegration = res.length > 0;

        if (hasIntegration) {
          this.getConfig();
        } else {
          this.setState({
            isLoaded: true,
            hasIntegration,
          });
        }
      });
  }

  onCreateConnection = () => {
    this.setState({
      isLoaded: false,
    }, () => {
      this.getConfig();
    });
  }

  renderContent = () => {
    const { hasIntegration, hasError, config } = this.state;
    const { preferredLanguage, isMainProfile, profileId, profileName } = this.props;
    const preferredLanguageSupported = !!SUPPORTED_LANGUAGES.find(l => l === preferredLanguage);

    if (hasError) {
      return (
        <GrayCard>
          <CardSection hasBorder={false}>
            <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
              <div style={{ flexShrink: 0 }}>
                <IconWarning.Large strokeClassName="stroke-color-gsMiddle" />
              </div>

              <div className="font-subhead font-color-gsMedDark" style={{ marginLeft: 12 }}>
                <FormattedMessage id="error.api.500-internal.message" />
              </div>
            </div>
          </CardSection>
        </GrayCard>
      );
    } else if (hasIntegration) {
      return (
        <ExistingDataConnection
          profileId={profileId}
          config={config}
          onLoaded={() => { this.setState({ isMedfusionLoaded: true }); }}
        />
      );
    } else {
      return (
        <NoExistingDataConnection
          onCreateConnection={this.onCreateConnection}
          preferredLanguageSupported={preferredLanguageSupported}
          isMainProfile={isMainProfile}
          profileName={profileName}
        />
      );
    }
  }

  renderFooter = () => {
    return (
      <EmptyCard>

        <span className="font-caption-2 bkgd-color-profile font-color-gsLightest" style={{ padding: '5px 10px', borderRadius: 20 }}>
          <FormattedMessage id="dataConnections.beta.icon.label" />
        </span>

        <Spacer size="4" />

      </EmptyCard>
    );
  }

  render() {
    const { isLoaded, isMedfusionLoaded, hasIntegration } = this.state;
    const isAllLoaded = isLoaded && (isMedfusionLoaded || !hasIntegration);

    return (
      <ContentWrapper isFlatContent={!hasIntegration}>
        <ContentLoader isContentLoaded={isAllLoaded} blockRenderUntilLoaded={!isLoaded} isFixed={true}>

          <div>
            {this.renderContent()}

            <Spacer size="12" />

            {isAllLoaded && this.renderFooter()}
          </div>
        </ContentLoader>
      </ContentWrapper>
    );
  }
}

function mapStateToProps() {
  return {};
}

function mapDispatchToProps(dispatch) {
  return {
    requestIntegrations: (profileId) => {
      return dispatch(integrationsActions.requestIntegrations(profileId));
    },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DataConnectionAllowed);
